import { FunctionComponent, useEffect, useState } from "react"
import type { CSSProperties } from "react"
import { Button, Drawer, Modal, Space, Tag, Tooltip, Typography, message, Select, Badge, Skeleton, DatePicker } from "antd"
import { LoadingOutlined, CloseOutlined } from "@ant-design/icons"
import { DateFormats, IAnnouncement, IUser, RoleHelper, useScreenSize, gen, swimminglyApi } from "./utils"
import { Colors } from "./ClubSeasonManagement/styles"
import { AnnouncementHelper } from "./Helpers/AnnouncementHelper"
import { sanitizeRichTextHtml } from "./utils/sanitizeRichTextHtml"
import moment from "moment"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPenToSquare, faUpRightAndDownLeftFromCenter, faCirclePlus, faMessage, faBoxArchive } from "@fortawesome/pro-regular-svg-icons"
import TiptapRichTextEditor from "./TiptapRichTextEditor"

const { Text } = Typography

type MessageHeaderProps  = {
    sender: string
    timestamp: moment.Moment
  }
  const MessageHeader: FunctionComponent<MessageHeaderProps>  = ({sender, timestamp}: MessageHeaderProps) => {
    const antDesignHalfWidth = 12
    return (<Space size={[antDesignHalfWidth,antDesignHalfWidth]} wrap>
        <div>{sender}</div>
        <div>{timestamp.format(DateFormats.monthDayYearTimestamp)}</div>
    </Space>)
  }

interface IClubCommunicationDrawerProps {
    visible: boolean
    user: IUser | null
    setVisible: (value: boolean) => void
    setMessageWindowVisible: (value: boolean) => void
}

export default function ClubCommunicationDrawer({
    visible,
    setVisible,
    setMessageWindowVisible,
    user
}: IClubCommunicationDrawerProps) {
    const [triggerRefresh, setTriggerRefresh] = useState(0)
    const [loading, setLoading] = useState(false)
    const [announcements, setAnnouncements] = useState<IAnnouncement[]>([])
    const [detailAnnouncement, setDetailAnnouncement] = useState<IAnnouncement | null>(null)
    const [detailVisible, setDetailVisible] = useState(false)
    const [editAnnouncement, setEditAnnouncement] = useState<IAnnouncement | null>(null)
    const [editHtml, setEditHtml] = useState("")
    const [editVisible, setEditVisible] = useState(false)
    const [editSubmitting, setEditSubmitting] = useState(false)
    const [emailLogVisible, setEmailLogVisible] = useState(false)
    const [emailLogLoading, setEmailLogLoading] = useState(false)
    const [emailLogRows, setEmailLogRows] = useState<any[]>([])
    const [emailLogMeta, setEmailLogMeta] = useState<any | null>(null)
    const [selectedClubId, setSelectedClubId] = useState<number | null>(null)
    const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(null)
    const [scheduledLoading, setScheduledLoading] = useState(false)
    const [scheduledAnnouncements, setScheduledAnnouncements] = useState<any[]>([])
    const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false)
    const [rescheduleTarget, setRescheduleTarget] = useState<any | null>(null)
    const [rescheduleValue, setRescheduleValue] = useState<any | null>(null)
    const [scheduledEditVisible, setScheduledEditVisible] = useState(false)
    const [scheduledEditTarget, setScheduledEditTarget] = useState<any | null>(null)
    const [scheduledEditHtml, setScheduledEditHtml] = useState("")
    const [scheduledEditSubmitting, setScheduledEditSubmitting] = useState(false)

    useEffect(() => {
        if (user && visible) {
            setLoading(true)
            new AnnouncementHelper()
                .getAnnouncements(user.userId)
                .then(( announcementResult ) => {
                    setLoading(false)
                    setAnnouncements(announcementResult)
                })
        }

     }, [user, visible, triggerRefresh])

    // When the active user changes (e.g., aliasing), clear any
    // announcement-related state so the drawer does not briefly
    // show data from the previous user.
    useEffect(() => {
        setAnnouncements([])
        setSelectedClubId(null)
        setSelectedSeasonId(null)
        setDetailAnnouncement(null)
        setDetailVisible(false)
        setEditAnnouncement(null)
        setEditVisible(false)
        setEmailLogVisible(false)
        setEmailLogRows([])
        setEmailLogMeta(null)
        setScheduledAnnouncements([])
        setScheduledLoading(false)
        setRescheduleModalVisible(false)
        setRescheduleTarget(null)
        setRescheduleValue(null)
        setScheduledEditVisible(false)
        setScheduledEditTarget(null)
        setScheduledEditHtml("")
        setScheduledEditSubmitting(false)
    }, [user?.userId])

    // Infer a sensible default club (and later season) for the filter
    useEffect(() => {
        if (!announcements || announcements.length === 0) {
            setSelectedClubId(null)
            return
        }

        if (selectedClubId !== null) return

        const validAnnouncements = announcements.filter((a) =>
            typeof a.clubId === "number" && Number.isFinite(a.clubId),
        )

        if (validAnnouncements.length === 0) {
            setSelectedClubId(null)
            return
        }

        const userClubId = user?.clubId ?? null

        let defaultClubId: number | null = null

        if (
            userClubId &&
            typeof userClubId === "number" &&
            validAnnouncements.some((a) => a.clubId === userClubId)
        ) {
            defaultClubId = userClubId
        } else {
            // Announcements are sorted newest-first; use the most recent club.
            defaultClubId = validAnnouncements[0].clubId
        }

        setSelectedClubId(defaultClubId)
    }, [announcements, user, selectedClubId])

    // When the selected club changes (or on first load), infer a default season
    useEffect(() => {
        if (!announcements || announcements.length === 0) {
            setSelectedSeasonId(null)
            return
        }

        if (selectedClubId === null) {
            setSelectedSeasonId(null)
            return
        }

        const annsForClub = announcements.filter((a) => a.clubId === selectedClubId)

        if (annsForClub.length === 0) {
            setSelectedSeasonId(null)
            return
        }

        if (
            selectedSeasonId !== null &&
            annsForClub.some((a) => a.seasonId === selectedSeasonId)
        ) {
            return
        }

        // Choose the season for this club that has the most recent announcement
        const latestBySeason = new Map<number, string>()

        for (const a of annsForClub) {
            if (!a || typeof a.seasonId !== "number") continue
            const existing = latestBySeason.get(a.seasonId)
            if (!existing) {
                latestBySeason.set(a.seasonId, a.created_at)
            } else if (moment(a.created_at).isAfter(moment(existing))) {
                latestBySeason.set(a.seasonId, a.created_at)
            }
        }

        let defaultSeasonId: number | null = null
        let defaultSeasonLatest: string | null = null

        latestBySeason.forEach((createdAt, seasonId) => {
            if (!defaultSeasonLatest) {
                defaultSeasonId = seasonId
                defaultSeasonLatest = createdAt
                return
            }
            if (moment(createdAt).isAfter(moment(defaultSeasonLatest))) {
                defaultSeasonId = seasonId
                defaultSeasonLatest = createdAt
            }
        })

        setSelectedSeasonId(defaultSeasonId)
    }, [announcements, selectedClubId, selectedSeasonId])

    const roleHelper = new RoleHelper(user?.role)
    const screenSize = useScreenSize()
    const isMobile: boolean = screenSize === "small"
    const drawerShellStyles = {
        body: {
            background: "linear-gradient(180deg, #f5f7fb 0%, #edf2f8 100%)",
            padding: 14,
        },
        header: {
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        },
    }
    const sectionSurfaceStyle: CSSProperties = {
        border: "1px solid rgba(15, 23, 42, 0.06)",
        borderRadius: 14,
        padding: 10,
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.95) 100%)",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.045)",
        backdropFilter: "blur(8px)",
    }

    const handleOpenDetail = (announcement: IAnnouncement) => {
        setDetailAnnouncement(announcement)
        setDetailVisible(true)

        // Debug: trace mark-as-read behavior
        // eslint-disable-next-line no-console
        console.log("[ClubCommunicationDrawer] marking announcement read", {
            announcementId: announcement.announcementId,
            readAt: announcement.readAt,
        })

        swimminglyApi
            .post(gen(`/api/messaging/announcements/${announcement.announcementId}/read`))
            .body({})
            .then((resp: any) => {
                // eslint-disable-next-line no-console
                console.log("[ClubCommunicationDrawer] mark-as-read response", resp)
                setAnnouncements((prev) =>
                    prev.map((a) =>
                        a.announcementId === announcement.announcementId
                            ? { ...a, readAt: new Date().toISOString() }
                            : a,
                    ),
                )

                try {
                    const evt = new CustomEvent("swimmingly:clubhouse-announcements:refetch")
                    document.dispatchEvent(evt)
                } catch {
                    // ignore
                }
            })
            .catch((err: any) => {
                // eslint-disable-next-line no-console
                console.error("[ClubCommunicationDrawer] mark-as-read error", err)
            })
    }

    const canEditAnnouncement = (announcement: IAnnouncement): boolean => {
        if (!user) return false
        if (!announcement.announcementMessageId) return false
        if (announcement.senderUserId !== user.userId) return false
        if (announcement.announcementUpdatedAt) return false

        const created = moment.tz(announcement.created_at, "UTC").local()
        const hoursSinceCreate = moment().diff(created, "hours", true)
        return hoursSinceCreate <= 24
    }

    const startEditAnnouncement = (announcement: IAnnouncement) => {
        setEditAnnouncement(announcement)
        setEditHtml(announcement.announcement || "")
        setEditVisible(true)
    }

    const performEditSave = (sendUpdateEmail: boolean) => {
        if (!editAnnouncement || !editAnnouncement.announcementMessageId) return
        const sanitized = sanitizeRichTextHtml(editHtml || "")
        if (!sanitized.trim()) {
            message.error("Announcement body cannot be empty.")
            return
        }

        const confirmText = sendUpdateEmail
            ? "This will update the announcement in the Clubhouse and send an updated email to all original recipients. Continue?"
            : "This will update the announcement in the Clubhouse only; no additional emails will be sent. Continue?"

        Modal.confirm({
            title: "Confirm announcement update",
            content: confirmText,
            okText: sendUpdateEmail ? "Update & send email" : "Update announcement",
            onOk: async () => {
                try {
                    setEditSubmitting(true)
                    const resp = await swimminglyApi
                        .post(
                            gen(
                                `/api/messaging/clubhouse-announcement/${encodeURIComponent(
                                    String(editAnnouncement.announcementMessageId),
                                )}/edit`,
                            ),
                        )
                        .body({
                            announcementHtml: sanitized,
                            sendUpdateEmail,
                        })

                    const data: any = (resp as any)?.data ?? resp ?? {}
                    if (data && data.status === "success") {
                        message.success(
                            sendUpdateEmail
                                ? "Your announcement has been updated and an email update has been sent."
                                : "Your announcement has been updated in the Clubhouse.",
                        )
                        setAnnouncements((prev) =>
                            prev.map((a) =>
                                a.announcementMessageId === editAnnouncement.announcementMessageId
                                    ? {
                                          ...a,
                                          announcement: sanitized,
                                          announcementUpdatedAt: new Date().toISOString(),
                                          readAt: null,
                                      }
                                    : a,
                            ),
                        )
                        setEditVisible(false)
                        setEditAnnouncement(null)

                        try {
                            const evt = new CustomEvent("swimmingly:clubhouse-announcements:refetch")
                            document.dispatchEvent(evt)
                        } catch {
                            // ignore
                        }
                    } else if (data && data.error) {
                        message.error(data.error)
                    } else {
                        message.error("Unable to update announcement right now.")
                    }
                } catch (err) {
                    console.error("performEditSave error", err)
                    message.error("Unable to update announcement right now.")
                } finally {
                    setEditSubmitting(false)
                }
            },
        })
    }

    const loadEmailLog = async (announcement: IAnnouncement) => {
        if (!announcement.announcementMessageId) return
        try {
            setEmailLogLoading(true)
            setEmailLogRows([])
            setEmailLogMeta(null)

            const resp = await swimminglyApi.get(
                gen(
                    `/api/messaging/clubhouse-announcement/${encodeURIComponent(
                        String(announcement.announcementMessageId),
                    )}/email-log`,
                ),
            )
            const data: any = (resp as any)?.data ?? resp ?? {}
            const logs: any[] = Array.isArray(data.logs) ? data.logs : []
            setEmailLogRows(logs)
            setEmailLogMeta(data.announcementMessage || null)
            setEmailLogVisible(true)
        } catch (err) {
            console.error("loadEmailLog error", err)
            message.error("Unable to load email log right now.")
        } finally {
            setEmailLogLoading(false)
        }
    }
    
    // Derive club and season options from the loaded announcements
    const clubIdToLabel = new Map<number, string>()
    for (const a of announcements || []) {
        if (!a || typeof a.clubId !== "number" || !Number.isFinite(a.clubId)) continue
        if (clubIdToLabel.has(a.clubId)) continue
        const label =
            a.clubNameLong || a.clubNameShort || (a.clubId ? `Club #${a.clubId}` : "Unknown club")
        clubIdToLabel.set(a.clubId, label)
    }

    const clubOptions = Array.from(clubIdToLabel.entries()).map(([clubId, label]) => ({
        value: clubId,
        label,
    }))

    const seasonIdToLabel = new Map<number, string>()
    for (const a of announcements || []) {
        if (!a || typeof a.seasonId !== "number" || !Number.isFinite(a.seasonId)) continue
        if (selectedClubId !== null && a.clubId !== selectedClubId) continue
        if (seasonIdToLabel.has(a.seasonId)) continue
        seasonIdToLabel.set(a.seasonId, a.seasonName || "Season")
    }

    const seasonOptions = Array.from(seasonIdToLabel.entries()).map(([seasonId, label]) => ({
        value: seasonId,
        label,
    }))

    const filteredAnnouncements = announcements.filter((a) => {
        if (!a) return false
        if (selectedClubId !== null && a.clubId !== selectedClubId) return false
        if (selectedSeasonId !== null && a.seasonId !== selectedSeasonId) return false
        return true
    })

    const loadScheduledAnnouncements = async (clubId: number | null, seasonId: number | null) => {
        if (!user || clubId === null || seasonId === null) {
            setScheduledAnnouncements([])
            return
        }

        const roleHelperLocal = new RoleHelper(user.role)
        const isAdmin =
            roleHelperLocal.isSwimminglyAdmin() ||
            roleHelperLocal.isClubAdmin() ||
            roleHelperLocal.isLeagueAdmin()

        if (!isAdmin) {
            setScheduledAnnouncements([])
            return
        }

        try {
            setScheduledLoading(true)
            setScheduledAnnouncements([])

            const resp = await swimminglyApi.get(
                gen(
                    `/api/messaging/clubhouse-announcement/scheduled?clubId=${encodeURIComponent(
                        String(clubId),
                    )}&seasonId=${encodeURIComponent(String(seasonId))}`,
                ),
            )
            const data: any = (resp as any)?.data ?? resp ?? {}
            const anns: any[] = Array.isArray(data.announcements)
                ? data.announcements
                : []
            setScheduledAnnouncements(anns)
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("ClubCommunicationDrawer: failed to load scheduled announcements", err)
        } finally {
            setScheduledLoading(false)
        }
    }

    useEffect(() => {
        if (!visible) return
        loadScheduledAnnouncements(selectedClubId, selectedSeasonId)
    }, [visible, selectedClubId, selectedSeasonId])

    return (
        <>
        <Drawer
            open={visible}
            rootClassName="profile-drawer-root"
            className="profile-drawer-rounded"
            title={null}
            closable={false}
            size={isMobile ? "92%" : 500}
            placement={isMobile ? "bottom" : "right"}
            onClose={() => {
                setLoading(false)
                setVisible(false)
                setTriggerRefresh(triggerRefresh + 1)
            }}
            styles={drawerShellStyles}
        >
            <div
                id="scrollableDiv"
                style={{ position: "relative", paddingTop: 8 }}
            >
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        zIndex: 10,
                    }}
                    onClick={() => {
                        setLoading(false)
                        setVisible(false)
                        setTriggerRefresh(triggerRefresh + 1)
                    }}
                />
                <div style={{textAlign:'center'}}>
                    <LoadingOutlined hidden={!loading} spin={true} />
                </div>
                <div
                    style={{
                        marginTop: 6,
                        marginBottom: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            alignItems: isMobile ? "flex-start" : "center",
                            justifyContent: "space-between",
                            gap: 8,
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    fontSize: 16,
                                    fontWeight: 600,
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faMessage}
                                    style={{
                                        fontSize: 18,
                                        width: 20,
                                        height: 20,
                                    }}
                                />
                                <span>Club Communication</span>
                            </div>
                            <div
                                style={{
                                    marginTop: 2,
                                    fontSize: 12,
                                    color: "#8c8c8c",
                                }}
                            >
                                Announcements from your club staff. Filter by club and season.
                            </div>
                        </div>
                        {(roleHelper.isClubAdmin() || roleHelper.isLeagueAdmin()) && (
                            isMobile ? (
                                <Button
                                    type="primary"
                                    icon={<FontAwesomeIcon icon={faCirclePlus} />}
                                    size="middle"
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        setMessageWindowVisible(true)
                                        setVisible(false)
                                    }}
                                >
                                    Create Announcement
                                </Button>
                            ) : (
                                <Tooltip
                                    title="Create a new announcement for all guardians & admins on your club to view here. They will also receive an email notification."
                                >
                                    <Button
                                        type="primary"
                                        icon={<FontAwesomeIcon icon={faCirclePlus} />}
                                        size="middle"
                                        onClick={() => {
                                            setMessageWindowVisible(true)
                                            setVisible(false)
                                        }}
                                    >
                                        Create Announcement
                                    </Button>
                                </Tooltip>
                            )
                        )}
                    </div>
                    {clubOptions.length > 0 && (
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                            }}
                        >
                            <Select
                                size="small"
                                style={{ minWidth: 160 }}
                                placeholder="Club"
                                value={selectedClubId ?? undefined}
                                onChange={(value: number) => {
                                    setSelectedClubId(value)
                                    // Reset season when club changes; season effect will infer a default.
                                    setSelectedSeasonId(null)
                                }}
                                options={clubOptions}
                            />
                            {seasonOptions.length > 0 && (
                                <Select
                                    size="small"
                                    style={{ minWidth: 160 }}
                                    placeholder="Season"
                                    value={selectedSeasonId ?? undefined}
                                    onChange={(value: number) => {
                                        setSelectedSeasonId(value)
                                    }}
                                    options={seasonOptions}
                                />
                            )}
                        </div>
                    )}
                </div>
                <div
                    style={sectionSurfaceStyle}
                >
                    {scheduledAnnouncements.length > 0 && (
                        <div
                            style={{
                                marginBottom: 8,
                                paddingBottom: 8,
                                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    justifyContent: "space-between",
                                    gap: 8,
                                    marginBottom: 4,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: "#1f1f1f",
                                    }}
                                >
                                    Scheduled announcements
                                </div>
                                {scheduledLoading && (
                                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                                        Loading…
                                    </div>
                                )}
                            </div>
                            {scheduledAnnouncements.length === 0 && !scheduledLoading && (
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: "#8c8c8c",
                                    }}
                                >
                                    No upcoming scheduled announcements for this club & season.
                                </div>
                            )}
                            {scheduledAnnouncements.length > 0 && (
                                <Space
                                    orientation="vertical"
                                    size="small"
                                    style={{ width: "100%" }}
                                >
                                    {scheduledAnnouncements.map((a) => {
                                        const scheduledLocal = a.scheduledAt
                                            ? moment.tz(a.scheduledAt, "UTC").local()
                                            : null
                                        const createdLocal = a.created_at
                                            ? moment.tz(a.created_at, "UTC").local()
                                            : null

                                        const whenLabel = scheduledLocal
                                            ? scheduledLocal.format(
                                                  `${DateFormats.monthDayYearTimestamp}`,
                                              )
                                            : "Scheduled"

                                        const handleCancel = async () => {
                                            if (!a.id) return
                                            Modal.confirm({
                                                title: "Cancel scheduled announcement?",
                                                content:
                                                    "This will prevent the announcement from being sent at its scheduled time.",
                                                okText: "Cancel scheduled send",
                                                okButtonProps: { danger: true },
                                                onOk: async () => {
                                                    try {
                                                        const resp = await swimminglyApi
                                                            .post(
                                                                gen(
                                                                    `/api/messaging/clubhouse-announcement/${encodeURIComponent(
                                                                        String(a.id),
                                                                    )}/cancel-scheduled`,
                                                                ),
                                                            )
                                                            .body({})

                                                        const data: any = (resp as any)?.data ?? resp ?? {}
                                                        if (data && data.status === "success") {
                                                            message.success(
                                                                "Scheduled announcement has been cancelled.",
                                                            )
                                                            setScheduledAnnouncements((prev) =>
                                                                prev.filter((row) => row.id !== a.id),
                                                            )
                                                        } else if (data && data.error) {
                                                            message.error(data.error)
                                                        } else {
                                                            message.error(
                                                                "Unable to cancel scheduled announcement right now.",
                                                            )
                                                        }
                                                    } catch (err) {
                                                        // eslint-disable-next-line no-console
                                                        console.error(
                                                            "ClubCommunicationDrawer: cancel scheduled announcement error",
                                                            err,
                                                        )
                                                        message.error(
                                                            "Unable to cancel scheduled announcement right now.",
                                                        )
                                                    }
                                                },
                                            })
                                        }

                                        return (
                                            <div
                                                key={a.id}
                                                style={{
                                                    padding: 10,
                                                    borderRadius: 12,
                                                    border: "1px solid rgba(15, 23, 42, 0.08)",
                                                    background:
                                                        "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                                                    boxShadow:
                                                        "0 6px 18px rgba(15, 23, 42, 0.04)",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "flex-start",
                                                        gap: 8,
                                                    }}
                                                >
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div
                                                            style={{
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                color: "#1f1f1f",
                                                            }}
                                                        >
                                                            Scheduled · {whenLabel}
                                                        </div>
                                                        <div
                                                            style={{
                                                                marginTop: 2,
                                                                fontSize: 11,
                                                                color: "#8c8c8c",
                                                            }}
                                                        >
                                                            {a.senderName || "Club staff"}
                                                            {createdLocal && (
                                                                <span>
                                                                    {" · Created "}
                                                                    {createdLocal.format(
                                                                        DateFormats.monthDayYearTimestamp,
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {a.bodyHtml && (
                                                            <div
                                                                style={{
                                                                    marginTop: 4,
                                                                    fontSize: 12,
                                                                    color: "#595959",
                                                                    display: "-webkit-box",
                                                                    WebkitLineClamp: isMobile ? 2 : 3,
                                                                    WebkitBoxOrient: "vertical" as any,
                                                                    overflow: "hidden",
                                                                }}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: sanitizeRichTextHtml(
                                                                        a.bodyHtml || "",
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <div style={{ display: "flex", gap: 4 }}>
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            onClick={() => {
                                                                setScheduledEditTarget(a)
                                                                setScheduledEditHtml(a.bodyHtml || "")
                                                                setScheduledEditVisible(true)
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            onClick={() => {
                                                                setRescheduleTarget(a)
                                                                const initial = a.scheduledAt
                                                                    ? moment
                                                                          .tz(a.scheduledAt, "UTC")
                                                                          .local()
                                                                    : moment()
                                                                setRescheduleValue(initial)
                                                                setRescheduleModalVisible(true)
                                                            }}
                                                        >
                                                            Reschedule
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            danger
                                                            type="link"
                                                            onClick={handleCancel}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </Space>
                            )}
                        </div>
                    )}
                    {loading && announcements.length === 0 ? (
                        <Space orientation="vertical" size="small" style={{ width: "100%" }}>
                            <Skeleton active title={false} paragraph={{ rows: isMobile ? 2 : 3 }} />
                            <Skeleton active title={false} paragraph={{ rows: isMobile ? 2 : 3 }} />
                            <Skeleton active title={false} paragraph={{ rows: isMobile ? 2 : 3 }} />
                        </Space>
                    ) : announcements.length === 0 ? (
                        <div
                            style={{
                                padding: 8,
                                textAlign: "center",
                                fontSize: 12,
                                color: "#8c8c8c",
                            }}
                        >
                            No announcements yet. When your club staff posts an announcement, it will appear here.
                        </div>
                    ) : filteredAnnouncements.length === 0 ? (
                        <div
                            style={{
                                padding: 8,
                                textAlign: "center",
                                fontSize: 12,
                                color: "#8c8c8c",
                            }}
                        >
                            No announcements for this club &amp; season. Try another season or club.
                        </div>
                    ) : (
                        <>
                        <Space orientation="vertical" size="small" style={{ width: "100%" }}>
                        {filteredAnnouncements.map((announcement) => {
                            const isUnread = !announcement.readAt
                            const isEdited = Boolean(announcement.announcementUpdatedAt)
                            const createdMoment = moment
                                .tz(announcement.created_at, "UTC")
                                .local()
                            const canEdit = canEditAnnouncement(announcement)

                            const announcementRoleHelper = new RoleHelper(announcement.role)
                            const roleName = announcementRoleHelper.roleName()
                            const title = announcement.title || null
                            const clubLabel = announcementRoleHelper.isSwimminglyAdmin()
                                ? ""
                                : announcement.clubNameLong ||
                                  announcement.clubNameShort ||
                                  (announcement.clubId ? `Club #${announcement.clubId}` : "Club")

                            const cardStyle: CSSProperties = {
                                padding: 10,
                                borderRadius: 14,
                                background: "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
                                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                                borderRight: "1px solid rgba(15, 23, 42, 0.08)",
                                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                                borderLeft: "3px solid rgba(148, 163, 184, 0.24)",
                                cursor: "pointer",
                                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.045)",
                                ...(isUnread
                                    ? {
                                          background:
                                              "linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%)",
                                          borderLeft: `3px solid ${Colors.announcementBlue}`,
                                      }
                                    : {}),
                            }

                            const bodyHtml = sanitizeRichTextHtml(announcement.announcement || "")

                            const bodyStyle: CSSProperties = {
                                marginTop: 8,
                                display: "-webkit-box",
                                WebkitLineClamp: isMobile ? 2 : 3,
                                WebkitBoxOrient: "vertical" as any,
                                overflow: "hidden",
                            }

                            const headerContent = (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 8,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 500 }}>
                                            {announcement.senderName}
                                            <span
                                                style={{
                                                    marginLeft: 8,
                                                    fontWeight: 400,
                                                    color: "#8c8c8c",
                                                }}
                                            >
                                                {createdMoment.format(DateFormats.monthDayYearTimestamp)}
                                            </span>
                                        </div>
                                        <div style={{ marginTop: 4 }}>
                                            <Tag color="geekblue">
                                                {title || roleName}
                                            </Tag>
                                        </div>
                                        <div style={{ marginTop: 2, fontSize: 12, color: "#8c8c8c" }}>
                                            {[roleName, clubLabel].filter(Boolean).join(" • ")}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                                paddingTop: 2,
                                        }}
                                    >
                                        {isEdited && (
                                            <Tag
                                                color="default"
                                                style={{
                                                    borderColor: "#faad14",
                                                    color: "#faad14",
                                                    backgroundColor: "#fffbe6",
                                                }}
                                            >
                                                Updated
                                            </Tag>
                                        )}
                                        <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
                                        {canEdit && (
                                            <Tooltip title="Edit announcement">
                                                <span
                                                    style={{ cursor: "pointer" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        startEditAnnouncement(announcement)
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            )

                            const bodyContent = (
                                <div
                                    style={bodyStyle}
                                    dangerouslySetInnerHTML={{
                                        __html: bodyHtml,
                                    }}
                                />
                            )

                            const cardContent = (
                                <div
                                    style={cardStyle}
                                    onClick={() => handleOpenDetail(announcement)}
                                >
                                    {isMobile ? (
                                        <>
                                            {headerContent}
                                            {bodyContent}
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 11,
                                                    color: "#8c8c8c",
                                                }}
                                            >
                                                Tap to view full announcement
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {headerContent}
                                            {bodyContent}
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 11,
                                                    color: "#8c8c8c",
                                                }}
                                            >
                                                Click to view full announcement
                                            </div>
                                        </>
                                    )}
                                </div>
                            )

                            return isUnread ? (
                                <Badge.Ribbon
                                    key={announcement.announcementId}
                                    text="New "
                                    color="blue"
                                >
                                    {cardContent}
                                </Badge.Ribbon>
                            ) : (
                                <div key={announcement.announcementId}>{cardContent}</div>
                            )
                        })}
                    </Space>
                    {filteredAnnouncements.length > 0 && (
                        <div
                            style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: 32,
                                background:
                                    "linear-gradient(to bottom, rgba(245,247,251,0), rgba(237,242,248,1))",
                                pointerEvents: "none",
                            }}
                        />
                    )}
                    </>
                    )}
                </div>
            </div>
        </Drawer>

        <Modal
            open={scheduledEditVisible && !!scheduledEditTarget}
            title="Edit scheduled announcement"
            onCancel={() => {
                if (!scheduledEditSubmitting) {
                    setScheduledEditVisible(false)
                    setScheduledEditTarget(null)
                    setScheduledEditHtml("")
                }
            }}
            footer={null}
            width={isMobile ? "90%" : 720}
        >
            {scheduledEditTarget && (
                <div>
                    {scheduledEditTarget.created_at && (
                        <div style={{ marginBottom: 8, fontSize: 12, color: "#595959" }}>
                            Created on
                            {" "}
                            {moment
                                .tz(scheduledEditTarget.created_at, "UTC")
                                .local()
                                .format(DateFormats.monthDayYearTimestamp)}
                        </div>
                    )}
                    <div
                        style={{
                            marginBottom: 8,
                            padding: 8,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#ffffff",
                        }}
                    >
                        <div style={{ marginBottom: 4, fontWeight: 500 }}>Message</div>
                        <div
                            style={{
                                borderRadius: 4,
                                border: "1px solid #d9d9d9",
                                backgroundColor: "#ffffff",
                                padding: 4,
                            }}
                        >
                            <TiptapRichTextEditor
                                value={scheduledEditHtml}
                                onChange={setScheduledEditHtml}
                                placeholder="Update your announcement before it is sent."
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            marginTop: 16,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 8,
                        }}
                    >
                        <Button
                            onClick={() => {
                                if (!scheduledEditSubmitting) {
                                    setScheduledEditVisible(false)
                                    setScheduledEditTarget(null)
                                    setScheduledEditHtml("")
                                }
                            }}
                            disabled={scheduledEditSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={scheduledEditSubmitting}
                            onClick={async () => {
                                if (!scheduledEditTarget || !scheduledEditTarget.id) return

                                const sanitized = sanitizeRichTextHtml(
                                    scheduledEditHtml || "",
                                )

                                if (!sanitized.trim()) {
                                    message.error(
                                        "Announcement body cannot be empty.",
                                    )
                                    return
                                }

                                try {
                                    setScheduledEditSubmitting(true)
                                    const resp = await swimminglyApi
                                        .post(
                                            gen(
                                                `/api/messaging/clubhouse-announcement/${encodeURIComponent(
                                                    String(scheduledEditTarget.id),
                                                )}/edit`,
                                            ),
                                        )
                                        .body({
                                            announcementHtml: sanitized,
                                            sendUpdateEmail: false,
                                        })

                                    const data: any = (resp as any)?.data ?? resp ?? {}

                                    if (data && data.status === "success") {
                                        message.success(
                                            "Scheduled announcement has been updated.",
                                        )
                                        setScheduledAnnouncements((prev) =>
                                            prev.map((row) =>
                                                row.id === scheduledEditTarget.id
                                                    ? { ...row, bodyHtml: sanitized }
                                                    : row,
                                            ),
                                        )
                                        setScheduledEditVisible(false)
                                        setScheduledEditTarget(null)
                                        setScheduledEditHtml("")
                                    } else if (data && data.error) {
                                        message.error(data.error)
                                    } else {
                                        message.error(
                                            "Unable to update scheduled announcement right now.",
                                        )
                                    }
                                } catch (err) {
                                    // eslint-disable-next-line no-console
                                    console.error(
                                        "ClubCommunicationDrawer: edit scheduled announcement error",
                                        err,
                                    )
                                    message.error(
                                        "Unable to update scheduled announcement right now.",
                                    )
                                } finally {
                                    setScheduledEditSubmitting(false)
                                }
                            }}
                        >
                            Save changes
                        </Button>
                    </div>
                </div>
            )}
        </Modal>

        <Modal
            open={rescheduleModalVisible && !!rescheduleTarget}
            title="Reschedule announcement"
            okText="Update schedule"
            cancelText="Cancel"
            onCancel={() => {
                setRescheduleModalVisible(false)
                setRescheduleTarget(null)
                setRescheduleValue(null)
            }}
            onOk={async () => {
                if (!rescheduleTarget || !rescheduleTarget.id) {
                    setRescheduleModalVisible(false)
                    setRescheduleTarget(null)
                    setRescheduleValue(null)
                    return
                }

                if (!rescheduleValue) {
                    message.error("Choose a new date and time for this announcement.")
                    return
                }

                const value: any = rescheduleValue
                const scheduledDate: Date =
                    value && typeof value.toDate === "function"
                        ? value.toDate()
                        : value instanceof Date
                        ? value
                        : new Date(value)

                if (!scheduledDate || Number.isNaN(scheduledDate.getTime())) {
                    message.error("Please choose a valid date and time.")
                    return
                }

                const now = new Date()
                const minDate = new Date(now.getTime() + 5 * 60 * 1000)
                const maxDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

                if (scheduledDate < minDate) {
                    message.error("Scheduled time must be at least 5 minutes from now.")
                    return
                }

                if (scheduledDate > maxDate) {
                    message.error("Scheduled time must be within the next 7 days.")
                    return
                }

                const iso = scheduledDate.toISOString()

                try {
                    const resp = await swimminglyApi
                        .post(
                            gen(
                                `/api/messaging/clubhouse-announcement/${encodeURIComponent(
                                    String(rescheduleTarget.id),
                                )}/reschedule`,
                            ),
                        )
                        .body({ scheduledAt: iso })

                    const data: any = (resp as any)?.data ?? resp ?? {}

                    if (data && data.status === "success") {
                        const serverIso =
                            typeof data.scheduledAt === "string" && data.scheduledAt
                                ? data.scheduledAt
                                : iso

                        message.success(
                            "Scheduled time has been updated for this announcement.",
                        )

                        setScheduledAnnouncements((prev) =>
                            prev.map((row) =>
                                row.id === rescheduleTarget.id
                                    ? { ...row, scheduledAt: serverIso }
                                    : row,
                            ),
                        )
                        setRescheduleModalVisible(false)
                        setRescheduleTarget(null)
                        setRescheduleValue(null)
                    } else if (data && data.error) {
                        message.error(data.error)
                    } else {
                        message.error("Unable to update scheduled time right now.")
                    }
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(
                        "ClubCommunicationDrawer: reschedule announcement error",
                        err,
                    )
                    message.error("Unable to update scheduled time right now.")
                }
            }}
            width={isMobile ? "90%" : 400}
        >
            <p style={{ fontSize: 12, marginBottom: 8 }}>
                Choose a new date and time for this announcement. It will be posted
                to the Clubhouse communication drawer and emailed at the updated
                time.
            </p>
            <DatePicker
	            showTime={{ use12Hours: true, format: "h:mm a" }}
                style={{ width: "100%" }}
                value={rescheduleValue as any}
                    onChange={(value) => setRescheduleValue(value)}
                    disabledDate={(current: any) => {
                        if (!current) return false

                        const value: Date =
                            current && typeof current.toDate === "function"
                                ? current.toDate()
                                : current instanceof Date
                                ? current
                                : new Date(current)

                        if (!value || Number.isNaN(value.getTime())) {
                            return true
                        }

                        const now = new Date()
                        const today = new Date(
                            now.getFullYear(),
                            now.getMonth(),
                            now.getDate(),
                        )
                        const maxDate = new Date(today)
                        maxDate.setDate(maxDate.getDate() + 7)

                        const candidate = new Date(
                            value.getFullYear(),
                            value.getMonth(),
                            value.getDate(),
                        )

                        return candidate < today || candidate > maxDate
                    }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}>
                You can schedule announcements between 5 minutes and 7 days from now.
            </div>
        </Modal>

        <Modal
            open={detailVisible && !!detailAnnouncement}
            title={detailAnnouncement ? "Club Announcement" : "Announcement"}
            footer={null}
            onCancel={() => {
                setDetailVisible(false)
                setDetailAnnouncement(null)
            }}
            width={isMobile ? "90%" : 600}
            styles={{
                body: {
                    background: "linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%)",
                    paddingTop: 16,
                },
            }}
        >
            {detailAnnouncement && (
                <div>
                    {(() => {
                        const detailRoleHelper = new RoleHelper(detailAnnouncement.role)
                        const detailRoleName = detailRoleHelper.roleName()
                        const detailTitle = detailAnnouncement.title || null
                        const detailClubLabel = detailRoleHelper.isSwimminglyAdmin()
                            ? ""
                            : detailAnnouncement.clubNameLong ||
                              detailAnnouncement.clubNameShort ||
                              (detailAnnouncement.clubId ? `Club #${detailAnnouncement.clubId}` : "Club")
                        const sentLabel = moment
                            .tz(detailAnnouncement.created_at, "UTC")
                            .local()
                            .format(DateFormats.monthDayYearTimestamp)
                        const editedLabel = detailAnnouncement.announcementUpdatedAt
                            ? moment
                                  .tz(detailAnnouncement.announcementUpdatedAt, "UTC")
                                  .local()
                                  .format(DateFormats.monthDayYearTimestamp)
                            : null

                        return (
                            <>
                                <div
                                    style={{
                                        marginBottom: 14,
                                        borderRadius: 16,
                                        padding: isMobile ? 16 : 18,
                                        background:
                                            "linear-gradient(180deg, #ffffff 0%, #f7faff 100%)",
                                        border: "1px solid rgba(15, 23, 42, 0.08)",
                                        boxShadow: "0 10px 26px rgba(15, 23, 42, 0.06)",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                            color: "#667085",
                                            marginBottom: 8,
                                        }}
                                    >
                                        Message from your club leadership
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 12,
                                        }}
                                    >
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div
                                                style={{
                                                    fontSize: isMobile ? 22 : 24,
                                                    lineHeight: 1.15,
                                                    fontWeight: 700,
                                                    color: "#101828",
                                                    marginBottom: 8,
                                                }}
                                            >
                                                {detailTitle || "Club announcement"}
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    marginBottom: 8,
                                                }}
                                            >
                                                <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
                                                    {detailRoleName}
                                                </Tag>
                                                {detailClubLabel ? (
                                                    <Text type="secondary">{detailClubLabel}</Text>
                                                ) : null}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    color: "#475467",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                Shared by <strong>{detailAnnouncement.senderName}</strong>
                                                {` on ${sentLabel}`}
                                            </div>
                                            {editedLabel ? (
                                                <div
                                                    style={{
                                                        marginTop: 4,
                                                        fontSize: 12,
                                                        color: "#667085",
                                                    }}
                                                >
                                                    Updated on {editedLabel}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        marginTop: 8,
                                        borderRadius: 16,
                                        padding: isMobile ? 16 : 20,
                                        background:
                                            "linear-gradient(180deg, #ffffff 0%, #fcfdff 100%)",
                                        border: "1px solid rgba(15, 23, 42, 0.08)",
                                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                            color: "#667085",
                                            marginBottom: 10,
                                        }}
                                    >
                                        Announcement
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 15,
                                            lineHeight: 1.75,
                                            color: "#1f2937",
                                            wordBreak: "break-word",
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeRichTextHtml(detailAnnouncement.announcement || ""),
                                        }}
                                    />
                                </div>
                            </>
                        )
                    })()}

                    {user && detailAnnouncement.senderUserId === user.userId && (
                        <div
                            style={{
                                marginTop: 12,
                                fontSize: 11,
                                color: "#667085",
                                paddingLeft: 2,
                            }}
                        >
                            You can edit this announcement once within 24 hours of sending.
                        </div>
                    )}
                    <div
                        style={{
                            marginTop: 16,
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                            flexDirection: isMobile ? "column" : "row",
                            alignItems: isMobile ? "stretch" : "center",
                            paddingTop: 8,
                        }}
                    >
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {detailAnnouncement && user &&
                                detailAnnouncement.senderUserId === user.userId &&
                                detailAnnouncement.announcementMessageId && (
                                    <Button
                                        size="small"
                                        type="link"
                                        danger
                                        icon={<FontAwesomeIcon icon={faBoxArchive} />}
                                        onClick={() => {
                                            const target = detailAnnouncement
                                            if (!target || !target.announcementMessageId) return

                                            Modal.confirm({
                                                title: "Archive this announcement?",
                                                content:
                                                    "Archiving will remove this announcement from the Clubhouse for all recipients. It will not retract any emails that were already sent.",
                                                okText: "Archive announcement",
                                                okButtonProps: { danger: true },
                                                onOk: async () => {
                                                    try {
                                                        const resp = await swimminglyApi
                                                            .post(
                                                                gen(
                                                                    `/api/messaging/clubhouse-announcement/${encodeURIComponent(
                                                                        String(target.announcementMessageId),
                                                                    )}/archive`,
                                                                ),
                                                            )
                                                            .body({})

                                                        const data: any = (resp as any)?.data ?? resp ?? {}
                                                        if (data && data.status === "success") {
                                                            message.success(
                                                                "Announcement archived from the Clubhouse.",
                                                            )
                                                            setAnnouncements((prev) =>
                                                                prev.filter(
                                                                    (a) =>
                                                                        a.announcementMessageId !==
                                                                        target.announcementMessageId,
                                                                ),
                                                            )
                                                            setDetailVisible(false)
                                                            setDetailAnnouncement(null)

                                                            try {
                                                                const evt = new CustomEvent(
                                                                    "swimmingly:clubhouse-announcements:refetch",
                                                                )
                                                                document.dispatchEvent(evt)
                                                            } catch {
                                                                // ignore
                                                            }
                                                        } else if (data && data.error) {
                                                            message.error(data.error)
                                                        } else {
                                                            message.error(
                                                                "Unable to archive announcement right now.",
                                                            )
                                                        }
                                                    } catch (err) {
                                                        console.error("archive announcement error", err)
                                                        message.error(
                                                            "Unable to archive announcement right now.",
                                                        )
                                                    }
                                                },
                                            })
                                        }}
                                    >
                                        Archive from Clubhouse
                                    </Button>
                                )}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {detailAnnouncement && (() => {
                                const isSender = user && detailAnnouncement.senderUserId === user.userId
                                const canViewLog =
                                    isSender ||
                                    roleHelper.isSwimminglyAdmin() ||
                                    roleHelper.isClubAdmin() ||
                                    roleHelper.isLeagueAdmin()

                                if (!canViewLog || !detailAnnouncement.announcementMessageId) {
                                    return null
                                }

                                return (
                                    <Button
                                        size="small"
                                        type="link"
                                        onClick={() => {
                                            if (!detailAnnouncement) return
                                            loadEmailLog(detailAnnouncement)
                                        }}
                                    >
                                        View email log
                                    </Button>
                                )
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </Modal>

        <Modal
            open={editVisible && !!editAnnouncement}
            title="Edit announcement"
            onCancel={() => {
                if (!editSubmitting) {
                    setEditVisible(false)
                    setEditAnnouncement(null)
                }
            }}
            footer={null}
            width={isMobile ? "90%" : 720}
        >
            {editAnnouncement && (
                <div>
                    <div style={{ marginBottom: 8, fontSize: 12, color: "#595959" }}>
                        Created on {moment
                            .tz(editAnnouncement.created_at, "UTC")
                            .local()
                            .format(DateFormats.monthDayYearTimestamp)}
                    </div>
                    <div
                        style={{
                            marginBottom: 8,
                            padding: 8,
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                            backgroundColor: "#ffffff",
                        }}
                    >
                        <div style={{ marginBottom: 4, fontWeight: 500 }}>Message</div>
                        <div
                            style={{
                                borderRadius: 4,
                                border: "1px solid #d9d9d9",
                                backgroundColor: "#ffffff",
                                padding: 4,
                            }}
                        >
                            <TiptapRichTextEditor
                                value={editHtml}
                                onChange={setEditHtml}
                                placeholder="Update your announcement (this will be visible in the Clubhouse and email)."
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            marginTop: 16,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 8,
                        }}
                    >
                        <Button onClick={() => setEditVisible(false)} disabled={editSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            type="default"
                            loading={editSubmitting}
                            onClick={() => performEditSave(false)}
                        >
                            Save &amp; Update
                        </Button>
                        <Button
                            type="primary"
                            loading={editSubmitting}
                            onClick={() => performEditSave(true)}
                        >
                            Save &amp; send update email
                        </Button>
                    </div>
                </div>
            )}
        </Modal>

        <Modal
            open={emailLogVisible}
            title="Announcement email history"
            onCancel={() => setEmailLogVisible(false)}
            footer={null}
            width={isMobile ? "90%" : 720}
        >
            {emailLogLoading && <div>Loading…</div>}
            {!emailLogLoading && (
                <div>
                    {emailLogMeta && (
                        <div style={{ marginBottom: 8, fontSize: 12, color: "#595959" }}>
                            <div>
                                Created on {moment
                                    .tz(emailLogMeta.created_at, "UTC")
                                    .local()
                                    .format(DateFormats.monthDayYearTimestamp)}
                            </div>
                            {emailLogMeta.updated_at && (
                                <div>
                                    Edited on {moment
                                        .tz(emailLogMeta.updated_at, "UTC")
                                        .local()
                                        .format(DateFormats.monthDayYearTimestamp)}
                                </div>
                            )}
                        </div>
                    )}
                    {emailLogRows.length === 0 ? (
                        <div>No emails have been recorded for this announcement yet.</div>
                    ) : (
                        <div style={{ maxHeight: 360, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                            {emailLogRows.map((row) => {
                                const typeLabel =
                                    row.emailType === "clubhouse_announcement_update"
                                        ? "Update"
                                        : "Original"

                                const formatTs = (ts: string | null | undefined) =>
                                    ts
                                        ? moment
                                              .tz(ts, "UTC")
                                              .local()
                                              .format("M/D/YY, h:mm A")
                                        : "-"

                                const recipientName: string | null = row.recipientName || null
                                const recipientEmail: string | null = row.recipientEmail || row.toAddress || null
                                const swimmersArr: any[] = Array.isArray(row.swimmers)
                                    ? row.swimmers
                                    : []

                                const swimmerNames = swimmersArr
                                    .map((s) => {
                                        if (!s) return null
                                        if (s.displayName) return s.displayName
                                        const parts = [s.firstName, s.lastName].filter(Boolean)
                                        const joined = parts.join(" ")
                                        return joined || null
                                    })
                                    .filter(Boolean)

                                return (
                                    <div
                                        key={row.id}
                                        style={{
                                            borderRadius: 4,
                                            border: "1px solid #f0f0f0",
                                            padding: 8,
                                            backgroundColor: "#ffffff",
                                        }}
                                    >
                                        <div style={{ fontWeight: 500 }}>
                                            {recipientName || recipientEmail || "Unknown recipient"}
                                        </div>
                                        {recipientName && recipientEmail && (
                                            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{recipientEmail}</div>
                                        )}
                                        {!recipientName && recipientEmail && (
                                            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{recipientEmail}</div>
                                        )}
                                        {swimmerNames.length > 0 && (
                                            <div style={{ marginTop: 2, fontSize: 11, color: "#8c8c8c" }}>
                                                Swimmers: {swimmerNames.join(", ")}
                                            </div>
                                        )}
                                        <div style={{ marginTop: 6, fontSize: 11 }}>
                                            <div>Type: {typeLabel}</div>
                                            <div>Sent: {formatTs(row.createdAt)}</div>
                                            <div>Delivered: {formatTs(row.deliveredAt)}</div>
                                            <div>Opened: {formatTs(row.openedAt)}</div>
                                            <div>Bounced: {formatTs(row.bouncedAt)}</div>
                                            <div>Dropped: {formatTs(row.droppedAt)}</div>
                                            <div>Unsubscribed: {formatTs(row.unsubscribedAt)}</div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}
        </Modal>
        </>
    )
}


